import { inject, Injectable } from '@angular/core';
import { ForecastVersionResponse } from '@api-response-types/forecast-version.response';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { ForecastProgressSignalService } from '@core/sse/forecast-progress-signal.service';
import { SseReconnectHandler } from '@core/sse/sse-reconnect-handler';
import { Store } from '@ngrx/store';
import { ForecastVersionHttpClient } from 'demand-forecasting/services/http/forecast-version-http-client.service';
import { ForecastVersionActions } from './forecast-version.actions';
import { selectAllForecastVersions } from './forecast-version.selectors';

// on sse reconnect, refetch the versions the client still believes are running,
// because a terminal progress event may have been missed while disconnected
@Injectable()
export class ForecastProgressReconnectHandler implements SseReconnectHandler {
  private readonly httpClient = inject(ForecastVersionHttpClient);
  private readonly forecastProgressSignal = inject(
    ForecastProgressSignalService,
  );
  private readonly store = inject(Store);
  private readonly forecastVersions = this.store.selectSignal(
    selectAllForecastVersions,
  );

  onReconnect(): void {
    const runningIds = this.collectRunningVersionIds();

    if (runningIds.length === 0) {
      return;
    }

    this.httpClient.getForecastVersionsByIds(runningIds).subscribe({
      next: (versions) => {
        for (const version of versions) {
          this.applyFreshVersion(version);
        }
      },
      error: () => undefined,
    });
  }

  private collectRunningVersionIds(): string[] {
    const ids = new Set<string>();

    for (const version of this.forecastProgressSignal.updates().values()) {
      if (this.isRunning(version.status)) {
        ids.add(version.id);
      }
    }

    for (const version of this.readStoreVersions()) {
      if (this.isRunning(version.status)) {
        ids.add(version.id);
      }
    }

    return [...ids];
  }

  private applyFreshVersion(version: ForecastVersionResponse): void {
    this.forecastProgressSignal.update(version);

    const existsInStore = this.readStoreVersions().some(
      (v) => v.id === version.id,
    );

    if (existsInStore) {
      this.store.dispatch(
        ForecastVersionActions.setForecastVersion({ forecastVersion: version }),
      );
    }
  }

  private readStoreVersions(): ForecastVersionResponse[] {
    try {
      return this.forecastVersions();
    } catch {
      return [];
    }
  }

  private isRunning(status: ForecastVersionStatus): boolean {
    return (
      status !== ForecastVersionStatus.FINISHED &&
      status !== ForecastVersionStatus.FAILED
    );
  }
}
